import React, { useEffect, useState } from "react";
import SectionHeading from "../../components/SectionHeading";
import { useNavigate } from "react-router-dom";
import FrequentlyUsedViews from "./FrequentlyUsedViews";
import Loader from "../../components/ui/Loader";
import { useSelector, useDispatch } from "react-redux";
import { useLazyGetReportDetailsQuery } from "../../slices/saveReportViewSlice";
import { useLazyGetPortalUserInfoQuery } from "../../slices/dataviewApiSlice";
import { useFlags } from "launchdarkly-react-client-sdk";
import GTM from "../../GTM";
import {
    filterDocumentDataViews,
    filterSelectedReport,
    getHighVolumeReport,
    getIsDocumentLibraryTileEnabled,
    getSelectedIsfOrganizationIds,
    getSubViewNames,
    getIsOldChangesTileEnabled,
    getIsNewLeaveChangesEnabled,
    filterChangesDataViews,
} from "../../utils/helpers";
import {
    changesReportType,
    DATAVIEWTYPE,
    documentsType,
    lifeType,
    REPORT_ITEMS,
} from "../../constants";
import {
    setPortalUserData,
    setReportDetails,
    setSelectedDataViewType,
    setSelectedReportViewId,
} from "../../slices/viewReportSlice";
import RequestFailed from "../../components/alerts/RequestFailed";
import PropTypes from "prop-types";

const ReportCard = React.memo(
    ({ title, icon, path, text, announcement, displayCards, reportData }) => {
        const gtm = new GTM(null);
        const dispatch = useDispatch();
        const navigate = useNavigate();
        const { user } = useSelector((state) => state.auth);
        const {
            displayDataViewTypes,
            displayDataViewTypesExternal,
            displayLifeForUsers,
            enableHighVolumeExport,
        } = useFlags();

        const {
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds,
            subViewNames,
        } = enableHighVolumeExport;

        const isHighVolumeReport = getHighVolumeReport({
            enableExternalUser,
            enableInternalUser,
            enableOrganizationList,
            isfUserIds,
            subViewNames,
            user,
        });

        const dataViewTypes = displayDataViewTypes.split(",");
        const dataViewTypesExternal = displayDataViewTypesExternal.split(",");
        const enabledUsersForLife = displayLifeForUsers?.split(",");

        const handleClick = () => {
            navigate(`/reportingcapabilities/${path}`);
            dispatch(setSelectedDataViewType(path));

            if (!isHighVolumeReport) {
                const selectedReportDetails = reportData.find((obj) => {
                    if (obj.DataViewType.includes(path.slice(0, 4))) {
                        return obj.Name;
                    }
                });

                dispatch(
                    setSelectedReportViewId(selectedReportDetails?.ViewId)
                );
            }

            gtm.Event(
                "run reports",
                "button",
                "click",
                title.toLowerCase(),
                "reports",
                "",
                "report_card_click",
                ""
            );
        };

        const handleKeyDown = (event) => {
            if (event.key === "Enter" || event.keyCode === 13) {
                navigate(`/reportingcapabilities/${path}`);
                dispatch(setSelectedDataViewType(path));
            }
        };

        const displayLifeCard = () => {
            return (
                (enabledUsersForLife.includes("All") ||
                    enabledUsersForLife?.includes(
                        user?.userId?.toLowerCase()
                    )) &&
                path === lifeType
            );
        };

        const isCardEnabled =
            (dataViewTypes.includes(path) && user?.isUserInternal) ||
            (dataViewTypesExternal.includes(path) && !user?.isUserInternal) ||
            displayLifeCard();

        const disabledStyle = {
            pointerEvents: "none",
            opacity: 0.4,
            cursor: "not-allowed",
        };

        const style = displayCards && !isCardEnabled ? disabledStyle : {};

        const buttonLabel = title.toLowerCase();

        return (
            <button
                className="section-card mb-0p report-card"
                onClick={handleClick}
                onKeyDown={handleKeyDown}
                tabIndex={displayCards ? 0 : -1}
                disabled={!displayCards}
                aria-disabled={!displayCards}
                style={style}
            >
                <div className="card-wrapper">
                    <div style={{ flex: 1 }}>
                        <div
                            className="d-flex"
                            style={{ gap: 8, alignItems: "center" }}
                        >
                            <div className={`${buttonLabel} icon`}>
                                <img src={icon} alt={buttonLabel} />
                            </div>

                            <SectionHeading heading={title} mb="mb-0p" />
                        </div>
                        {announcement?.length > 0 && (
                            <p
                                style={{
                                    textAlign: "left",
                                    marginBottom: 0,
                                    marginTop: 16,
                                }}
                                className="card-content"
                            >
                                {announcement.map((segment) =>
                                    segment.bold ? (
                                        <strong key={segment.text}>
                                            {segment.text}
                                        </strong>
                                    ) : (
                                        <span key={segment.text}>
                                            {segment.text}
                                        </span>
                                    )
                                )}
                            </p>
                        )}
                        <p
                            style={{
                                textAlign: "left",
                                marginBottom: 0,
                                marginTop: 16,
                            }}
                            className="card-content"
                        >
                            {text}
                        </p>
                    </div>

                    <span className="a-self" style={{ padding: "7px 0" }}>
                        <div className="d-flex button">
                            <span>View {buttonLabel}</span>
                            <i className="fa-solid fa-chevron-right"></i>
                        </div>
                    </span>
                </div>
            </button>
        );
    }
);

ReportCard.propTypes = {
    title: PropTypes.string.isRequired,
    icon: PropTypes.string.isRequired,
    path: PropTypes.string.isRequired,
    text: PropTypes.string.isRequired,
    announcement: PropTypes.arrayOf(
        PropTypes.shape({
            text: PropTypes.string,
            bold: PropTypes.bool,
        })
    ),
    displayCards: PropTypes.bool.isRequired,
    reportData: PropTypes.array.isRequired,
};

const ReportViews = ({ displayReportView, organizations, user }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const {
        displaySubViewReports,
        displayDocumentLibraryTile,
        displayoldChangesTile,
        displayNewLeaveChangesReport,
    } = useFlags();

    const {
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
    } = displaySubViewReports || {};

    const allOrganizations = useSelector(
        (state) => state?.userDetails?.organizations
    );

    const isDocumentLibraryTileEnabled = getIsDocumentLibraryTileEnabled({
        displayDocumentLibraryTile,
        user,
        allOrganizations,
    });

    const isOldChangesTileEnabled = getIsOldChangesTileEnabled({
        displayoldChangesTile,
        user,
        allOrganizations,
    });

    const isNewLeaveChangesEnabled = getIsNewLeaveChangesEnabled({
        displayNewLeaveChangesReport,
        user,
        allOrganizations,
    });

    const visibleReportItems = REPORT_ITEMS.filter((item) => {
        if (item.path === changesReportType) return isOldChangesTileEnabled;
        if (item.path === documentsType) return isDocumentLibraryTileEnabled;
        return true;
    });

    const [reportData, setReportData] = useState([]);

    const frequentlyUsedData = filterChangesDataViews(
        reportData,
        isOldChangesTileEnabled,
        isNewLeaveChangesEnabled
    );

    const {
        selectedOrganizationsReports: userSelectedOrganizationReports,
        selectedOrganizationsIdReports: reportId,
        selectedRelatedOrganizationsForReports: selectedRelatedOrganizations,
        portalUserData: storedPortalUserData,
    } = useSelector((state) => state.reportOrganization);

    const { totalParentOrganizations } = useSelector(
        (state) => state.organizations
    );
    const { selectedIsfOrganizationIds, organizationGroupId } =
        getSelectedIsfOrganizationIds(
            userSelectedOrganizationReports,
            reportId,
            selectedRelatedOrganizations,
            totalParentOrganizations
        );

    const { dlselectedIsfOrganizationId, dlViewId, dlDataView } = useSelector(
        (state) => state.auth
    );

    const [
        getPortalUser,
        { data: portalUserData, isError, isFetching: isFetchingUserData },
    ] = useLazyGetPortalUserInfoQuery();

    useEffect(() => {
        if (userSelectedOrganizationReports && portalUserData) {
            dispatch(setPortalUserData(portalUserData?.userPermissions));
        }
    }, [portalUserData]);

    const [
        getReportDetails,
        { data: reportDetailsData, isFetching, isError: isReportDetailsError },
    ] = useLazyGetReportDetailsQuery();

    useEffect(() => {
        if (reportDetailsData) {
            const updatedSelectedReport = filterDocumentDataViews(
                filterSelectedReport(
                    reportDetailsData,
                    subViewNames,
                    filterResult
                ),
                isDocumentLibraryTileEnabled
            );
            setReportData(updatedSelectedReport);
            dispatch(setReportDetails(updatedSelectedReport));
        }
    }, [reportDetailsData, isDocumentLibraryTileEnabled]);

    const filterResult = getSubViewNames({
        enableExternalUser,
        enableInternalUser,
        enableOrganizationList,
        isfUserIds,
        subViewNames,
        user,
    });

    const reportDetailsParams = {
        isfUserId: user.userId,
        dataViewType: DATAVIEWTYPE,
        selectedIsfOrganizationIds: selectedIsfOrganizationIds,
    };

    // Include organizationGroupId if it's defined
    if (organizationGroupId) {
        reportDetailsParams.organizationGroupId = organizationGroupId;
    }
    const relatedOrganizationIDISF =
        userSelectedOrganizationReports[reportId] &&
        userSelectedOrganizationReports[reportId]?.related?.map(
            (item) => item.ORGANIZATIONIDISF
        );

    const organizationIDISF = userSelectedOrganizationReports[reportId] && [
        userSelectedOrganizationReports[reportId]?.organization
            ?.ORGANIZATIONIDISF,
    ];

    const assignedOrgs = storedPortalUserData?.assignedOrgs?.map((obj) =>
        obj.orgId.toLowerCase()
    );

    const orgsToCompare =
        userSelectedOrganizationReports[reportId]?.related?.length > 0
            ? relatedOrganizationIDISF
            : organizationIDISF;

    const sortedAssignedOrgs = assignedOrgs?.sort();
    const sortedOrgsToCompare = orgsToCompare
        ?.map((org) => org?.toLowerCase())
        ?.sort();

    // Check if the arrays are of the same length and their elements match
    const areArraysEqual =
        sortedAssignedOrgs?.length === sortedOrgsToCompare?.length &&
        sortedAssignedOrgs?.every(
            (value, index) => value === sortedOrgsToCompare[index]
        );

    useEffect(() => {
        if (userSelectedOrganizationReports[reportId] && !areArraysEqual) {
            getPortalUser({
                isfUserId: String(user.userId).toUpperCase(),
                organizationId:
                    userSelectedOrganizationReports[reportId]?.related?.length >
                    0
                        ? relatedOrganizationIDISF
                        : organizationIDISF,
            });
        }
    }, [userSelectedOrganizationReports, areArraysEqual]);

    useEffect(() => {
        if (displayReportView || organizations.length === 1) {
            getReportDetails(reportDetailsParams);
        }
    }, [
        displayReportView,
        organizations.length,
        JSON.stringify(selectedIsfOrganizationIds),
    ]);

    useEffect(() => {
        if (
            Array.isArray(reportData) &&
            reportData.length > 0 &&
            dlselectedIsfOrganizationId &&
            dlViewId &&
            dlDataView
        ) {
            navigate(`/reportingcapabilities/${dlDataView}`);
            dispatch(setSelectedDataViewType(dlDataView));
            dispatch(setSelectedReportViewId(Number(dlViewId)));
        }
    }, [
        reportData,
        dlselectedIsfOrganizationId,
        dlViewId,
        dlDataView,
        navigate,
        portalUserData,
        isError,
    ]);

    return (
        <div>
            {isReportDetailsError ? (
                <RequestFailed />
            ) : (
                <div role="status">
                    {isFetching || isFetchingUserData ? (
                        <Loader />
                    ) : (
                        <div
                            className="report-views"
                            style={
                                displayReportView
                                    ? {}
                                    : {
                                          pointerEvents: "none",
                                          opacity: "0.4",
                                          gridColumn: 1 / 3,
                                          gridRow: 1 / 4,
                                      }
                            }
                        >
                            {visibleReportItems.map((item, index) => {
                                // When the last row holds a single card, let it span the full width
                                const isLastCardAlone =
                                    visibleReportItems.length % 3 === 1 &&
                                    index === visibleReportItems.length - 1;

                                return (
                                    <div
                                        key={item.path}
                                        className={
                                            isLastCardAlone
                                                ? "report-card-full-width"
                                                : ""
                                        }
                                    >
                                        <ReportCard
                                            key={item.path}
                                            {...item}
                                            reportData={reportData}
                                            portalUserData={
                                                portalUserData?.userPermissions
                                            }
                                            displayCards={displayReportView}
                                            isError={isError}
                                        />
                                    </div>
                                );
                            })}

                            <FrequentlyUsedViews
                                data={frequentlyUsedData}
                                isFetching={isFetching}
                                isError={isError}
                                portalUserData={portalUserData?.userPermissions}
                                displayReportView={displayReportView}
                            />
                        </div>
                    )}
                </div>
            )}{" "}
        </div>
    );
};

ReportViews.propTypes = {
    displayReportView: PropTypes.bool.isRequired,
    organizations: PropTypes.array.isRequired,
    user: PropTypes.shape({
        userId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
        isUserInternal: PropTypes.bool,
    }).isRequired,
};

export default ReportViews;
